import { Check, Star, TrendingUp, Users } from "lucide-react";

const tiers = [
  {
    name: "Standard Service",
    price: "$260 – $500",
    period: "/ month",
    tagline: "Established sole traders & small businesses (1–5 staff)",
    features: [
      "Up to 100 transactions per month",
      "Accounts payable & receivable maintenance",
      "Bi-monthly GST filing with IRD",
      "Standard financial reporting",
    ],
    highlight: false,
  },
  {
    name: "Growth / High-Volume",
    price: "$650 – $1,500+",
    period: "/ month",
    tagline: "Growing businesses with multi-card / bank accounts",
    features: [
      "Everything in Standard Service",
      "Up to 300+ transactions per month",
      "AP batch payment setups",
      "Monthly P&L reporting",
    ],
    highlight: true,
  },
];

const addons = [
  {
    icon: Users,
    name: "Payroll Add-On",
    price: "+$15",
    period: "/ employee / pay run",
    subtitle: "or $150 – $290 flat monthly",
    desc: "Payday filing with IRD, leave accrual tracking, KiwiSaver, and holiday pay calculations. Ideal for any employer with staff.",
  },
  {
    icon: TrendingUp,
    name: "Cashflow Management",
    price: "$600",
    period: "/ month",
    subtitle: "Proactive forecasting & monitoring",
    desc: "Know what's coming in, what's going out, and plan with confidence with ongoing cashflow forecasting and monitoring.",
  },
];

function PricingCard({ tier, index }) {
  const { name, price, period, tagline, features, highlight } = tier;
  return (
    <div
      data-testid={`pricing-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`rounded-md p-8 lg:p-10 fade-up ${
        highlight
          ? "bg-[#10242B] text-white relative"
          : "bg-white border border-[#E1E8E9] text-[#10242B]"
      }`}
      style={{ animationDelay: `${0.08 * index}s` }}
    >
      {highlight && (
        <span className="inline-flex items-center gap-1.5 bg-[#E8791F] text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-md mb-5">
          <Star size={13} className="fill-white" /> Most Popular
        </span>
      )}
      <h3 className="font-heading text-2xl font-semibold">{name}</h3>
      <p className={`mt-2 text-sm ${highlight ? "text-white/70" : "text-[#55636B]"}`}>
        Best for: {tagline}
      </p>
      <div className="mt-6 flex items-end gap-1.5 flex-wrap">
        <span className="font-heading text-4xl font-bold">{price}</span>
        <span className={`pb-1 text-sm ${highlight ? "text-white/70" : "text-[#55636B]"}`}>
          {period}
        </span>
      </div>

      <ul className="mt-7 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[15px]">
            <Check size={18} className="mt-0.5 shrink-0 text-[#10BFCB]" />
            <span className={highlight ? "text-white/90" : ""}>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href="#book"
        data-testid={`pricing-cta-${index}`}
        className={`mt-8 block text-center font-semibold px-6 py-3.5 rounded-md transition-colors ${
          highlight
            ? "bg-[#E8791F] text-white hover:bg-[#CE6410]"
            : "border-2 border-[#10242B] text-[#10242B] hover:bg-[#10242B] hover:text-white"
        }`}
      >
        Get Started
      </a>
    </div>
  );
}

function AddonCard({ addon, index }) {
  const { icon: Icon, name, price, period, subtitle, desc } = addon;
  return (
    <div
      data-testid={`addon-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="rounded-md bg-[#10242B] text-white p-8 flex flex-col fade-up"
      style={{ animationDelay: `${0.08 * index}s` }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#10BFCB] shrink-0">
          <Icon size={24} className="text-[#10242B]" />
        </div>
        <span className="inline-flex items-center bg-[#E8791F] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-md">
          Add-on
        </span>
      </div>
      <h4 className="mt-5 font-heading text-xl font-semibold">{name}</h4>
      <div className="mt-3 flex items-end gap-1.5 flex-wrap">
        <span className="font-heading text-3xl font-bold">{price}</span>
        <span className="pb-1 text-sm text-white/70">{period}</span>
      </div>
      <p className="mt-1 text-sm text-[#10BFCB] font-medium">{subtitle}</p>
      <p className="mt-4 text-white/75 leading-relaxed text-[15px] flex-1">{desc}</p>
      <a
        href="#book"
        data-testid={`addon-cta-${index}`}
        className="mt-6 inline-block text-center bg-[#E8791F] text-white font-semibold px-6 py-3.5 rounded-md hover:bg-[#CE6410] transition-colors"
      >
        Add This On
      </a>
    </div>
  );
}

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-[#0DA2AD]">
            Simple, Transparent Pricing
          </h4>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#10242B]">
            Packages that grow with you
          </h2>
          <p className="mt-5 text-lg text-[#55636B]">
            Straightforward monthly packages — no lock-in surprises. Choose the
            plan that fits where your business is now, and add payroll or cashflow
            support as you need it. Final package confirmed after a free
            consultation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-start">
          {tiers.map((t, i) => (
            <PricingCard key={t.name} tier={t} index={i} />
          ))}
        </div>

        <div className="mt-16">
          <h3 className="font-heading text-2xl font-semibold text-[#10242B] mb-6">
            Optional Add-Ons
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {addons.map((a, i) => (
              <AddonCard key={a.name} addon={a} index={i} />
            ))}
          </div>
        </div>

        <p
          data-testid="pricing-gst-note"
          className="mt-8 text-sm text-[#55636B] text-center"
        >
          All prices exclude GST. Final package confirmed after a free consultation.
        </p>
      </div>
    </section>
  );
}
